import { ComponentPropsWithoutRef, useEffect, useRef, useState } from "react";
import styles from "./GalleryVideoElement.module.scss";
import classNames from "classnames/bind";
import { GalleryVideoObject, VideoPreloadValue } from "../../utils";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";
import React from "react";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { useGalleryContext } from "@/components/Galleries/context/GalleryContext.tsx";

const cx = classNames.bind(styles);

export interface GalleryVideoElementProps
  extends ComponentPropsWithoutRef<"div"> {
  video: GalleryVideoObject;
  videoId: string;
  isModal?: boolean;
  isInView: boolean;
  videoPreload: VideoPreloadValue;
  carouselGalleryInpTest?: boolean;
  videoAutoplay?: boolean;
  setVideoAutoplay: (arg: boolean) => void;
}

export const GalleryVideoElement = ({
  video,
  videoId,
  isModal = false,
  isInView,
  className,
  videoPreload,
  carouselGalleryInpTest = false,
  videoAutoplay = false,
  setVideoAutoplay,
  ...props
}: GalleryVideoElementProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCustomPlayButtonVisible, setIsCustomPlayButtonVisible] =
    useState(true);
  const { flagEnabled } = useGalleryContext();
  const videoEnhancements = flagEnabled("videoEnhancements");

  const buttonClassNames = cx(styles.videoPlayButton, {
    hideButton: !isCustomPlayButtonVisible,
  });

  useEffect(() => {
    setIsCustomPlayButtonVisible(true);
  }, [video]);

  useEffect(() => {
    if (videoRef.current && videoAutoplay && isInView) {
      videoRef.current.play();
      setVideoAutoplay(false);
    }
  }, [videoAutoplay, isInView]);

  useEffect(() => {
    if (videoRef.current && !isInView) {
      videoRef.current.pause();
    }
  }, [isInView]);

  const handleVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
    dispatchGalleryMonetateQEvent(
      "VideoInteraction",
      isModal ? "videoModal" : "generic",
      undefined,
      carouselGalleryInpTest,
    );
  };

  const { baseUrl } = getBaseUrlAndQuery(video.imageUrl);

  return (
    // eslint-disable-next-line
    <div className={cx(styles.videoWrapper, className)} {...props}>
      {videoEnhancements && (
        <div
          data-testid="standard:gallery:video:play-overlay-button"
          onClick={handleVideoClick}
          className={cx(styles.videoPlayOverlay, {
            hideButton: !isCustomPlayButtonVisible,
          })}
        />
      )}

      <button
        data-testid="standard:gallery:video:play-button"
        aria-label="play video"
        className={buttonClassNames}
        tabIndex={isInView ? 0 : -1}
        onClick={handleVideoClick}
      />
      <video
        ref={videoRef}
        className={cx(styles.videoElement, {
          modal: isModal,
        })}
        data-testid="standard:gallery:video"
        src={video.videoUrl}
        controls={!isCustomPlayButtonVisible}
        onPlay={() => setIsCustomPlayButtonVisible(false)}
        poster={formatAmplienceImageURL(`${baseUrl}?wid=720&hei=640`)}
        tabIndex={isInView ? 0 : -1}
        preload={videoPreload}
        aria-label={video.title}
        autoPlay={videoAutoplay}
      />
    </div>
  );
};
